import {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { LoadingIcon } from 'nexticons/outline';
import { useSelector } from 'react-redux';
import { selectAdyenPaymentMethods } from 'containers/ReviewOrder/selectors';
import useAdyenPayment from 'helpers/hooks/useAdyenPayment';

const AdyenSaveCardDropin = forwardRef(function AdyenSaveCardDropin(
  {
    onSubmit,
    onAdditionalDetails,
    onBrand,
    onClick,
    isCardPaymentAvailable,
    shouldHide,
    isSuccess,
    ...restProps
  },
  ref
) {
  const adyenPaymentMethods = useSelector(selectAdyenPaymentMethods);
  const containerId = 'save-card-dropin-container';
  const dropinRef = useRef(null);

  const adyenConfig = useMemo(
    () => ({
      clientKey: window.storeConfigInitial.adyenClientKey,
      paymentMethods: adyenPaymentMethods,
      countryCode: window.storeConfigInitial.countryIdentifier,
      currency: window.storeConfigInitial.currencyCode,
      toPay: 0,
      isSaveCard: true,
      translations: {
        'en-US': {
          'paymentMethodsList.otherPayments.label': 'Add payment method',
          'storedPaymentMethod.disable.button': 'Delete',
          'storedPaymentMethod.disable.confirmation':
            'Delete stored payment method',
          'storedPaymentMethod.disable.confirmButton': 'Yes, delete',
        },
      },
    }),
    [adyenPaymentMethods]
  );

  const { checkout, mountComponent, isLoading } = useAdyenPayment(adyenConfig);
  const showLoading = !isSuccess && isLoading;

  const unMountDropin = () => {
    if (dropinRef.current) {
      try {
        dropinRef.current.unmount();
      } catch (err) {
        console.warn('Unmount failed:', err);
      }
    }
  };

  const resetDropin = async () => {
    if (checkout) {
      unMountDropin();
      dropinRef.current = await mountComponent({
        paymentMethod: 'dropin',
        containerId,
        onSubmit,
        onAdditionalDetails,
        onBrand,
        openFirstStoredPaymentMethod: false,
        showRemovePaymentMethodButton: true,
        disableFinalAnimation: true,
        ...restProps,
      });
    }
  };

  useImperativeHandle(ref, () => ({
    resetDropin,
  }));

  useEffect(() => {
    if (checkout) {
      resetDropin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout]);

  useEffect(() => {
    return () => {
      unMountDropin();
    };
  }, []);

  return (
    <div
      className={classNames({
        'relative mt-4 min-h-[200px]': isCardPaymentAvailable,
        'hidden-adyen': !isCardPaymentAvailable,
        hidden: shouldHide,
      })}
    >
      <div
        className={classNames(
          'absolute inset-0 flex flex-col items-center justify-center z-10 bg-white bg-opacity-80 rounded transition-opacity duration-300',
          {
            'opacity-0 pointer-events-none': !showLoading,
          }
        )}
      >
        <LoadingIcon strokeWidth={3} width={40} />
        <p className='mt-3 text-base text-black'>Loading payment methods...</p>
      </div>

      <div
        id={containerId}
        className={classNames('transition-opacity duration-300', {
          'opacity-0 pointer-events-none': showLoading,
        })}
        onClick={onClick}
      ></div>
    </div>
  );
});

export default AdyenSaveCardDropin;
